<script setup lang="ts">
import Breadcrumb from "@common/src/volt/Breadcrumb.vue";
import NavButton from "@common/src/volt/NavButton.vue";
import { CLASS_NAMES } from "@common/lib/constants/classes";
import {
  FILTERS_INJECTION_KEY,
} from "@common/user/composables/symbols";
import { useAuth } from "@common/user/composables/useAuth";
import { useAvailableCameras } from "@common/user/composables/useAvailableCameras";
import { useDynamoDBClients } from "@common/user/composables/useDynamoDBClient";
import { useEventCameras } from "@common/user/composables/useEventCameras";
import { useEventSectors } from "@common/user/composables/useEventSectors";
import { useFilters } from "@common/user/composables/useFilters";
import { useNowCustom } from "@common/user/composables/useNowCustom";
import { useProjectEvents } from "@common/user/composables/useProjectEvents";
import { useProjects } from "@common/user/composables/useProjects";
import { computed, provide, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterView, useRoute, useRouter } from "vue-router";
import LanguageSwitcher from "./components/LanguageSwitcher.vue";
import TimeController from "./components/TimeController.vue";

const router = useRouter();
const { locale } = useI18n();

const { credentials, login, logout, isLoggedIn } = useAuth(router);

const credentialsReset = computed(() => credentials.value?.timestamp || 0)
const dynamoDBClients = useDynamoDBClients(credentials);

const currentUserProjectIds = computed(() => credentials.value?.creds?.metadata?.project_ids || []);
const currentUserId = computed(() => credentials.value?.creds?.metadata?.user_id);

const allCamerasQuery = useAvailableCameras(
  computed(() => isLoggedIn.value ? dynamoDBClients.ReadAllCameras.value : null),
  credentialsReset
);

const route = useRoute();
const currentProjectId = computed(
  () => route.params.project_id as string | undefined,
);
const currentEventId = computed(
  () => route.params.event_id as string | undefined,
);
const showSettingsDrawer = ref(false);

// Fetch projects and events for project/event selection
const classes = ref([...CLASS_NAMES]);

const eventCameraIDsQuery = useEventCameras(
  computed(() => isLoggedIn.value ? currentProjectId.value : undefined),
  computed(() => isLoggedIn.value ? currentEventId.value : undefined),
  dynamoDBClients.ReadAuthorizedProjectData,
  credentialsReset
);

const projectsQuery = useProjects(
  computed(() => isLoggedIn.value ? currentUserId.value : undefined),
  computed(() => isLoggedIn.value ? currentUserProjectIds.value : []),
  dynamoDBClients.ReadAuthorizedProjectData,
  credentialsReset
);

// Derive project metadata from projectsQuery instead of a separate DynamoDB call
const projectMetadataQuery = {
  data: computed(() => {
    const projects = projectsQuery.data?.value;
    if (!projects || !currentProjectId.value) return undefined;
    return projects.find(p => p.project_id === currentProjectId.value) ?? undefined;
  }),
  isLoading: projectsQuery.isLoading,
  error: projectsQuery.error,
};

const eventsQuery = useProjectEvents(
  computed(() => isLoggedIn.value ? currentProjectId.value : undefined),
  dynamoDBClients.QueryEventsByProjectGSI,
  credentialsReset
);

const sectorsQuery = useEventSectors(
  computed(() => isLoggedIn.value ? currentProjectId.value : undefined),
  computed(() => isLoggedIn.value ? currentEventId.value : undefined),
  dynamoDBClients.ReadAuthorizedProjectData,
  credentialsReset
);

const filters = useFilters(
  currentProjectId,
  currentEventId,
  classes,
  projectMetadataQuery,
  eventsQuery,
  eventCameraIDsQuery,
  allCamerasQuery,
  sectorsQuery
);

const localhost = ref(window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

provide("localhost", localhost);
provide("credentialsQuery", credentials);
provide("projectsQuery", projectsQuery);
provide("projectMetadataQuery", projectMetadataQuery);
provide("eventsQuery", eventsQuery);
provide("auth", { login, logout });
provide("dynamoDBClients", dynamoDBClients);
provide("credentialsReset", credentialsReset);
provide("eventCameraIDsQuery", eventCameraIDsQuery);
provide("sectorsQuery", sectorsQuery);
provide(FILTERS_INJECTION_KEY, filters);

// Provide drawer state, available options, and queries to child components
provide("settingsDrawer", showSettingsDrawer);

const {
  selectedEventName,
  selectedProjectName,
} = filters;

// Track last selected map_id so the nav link preserves it across pages
const lastMapId = ref<string | undefined>(undefined);
watch(() => route.params.map_id, (mapId) => {
  if (mapId) lastMapId.value = mapId as string;
});

// Navigation computed properties for event pages
const isOnEventStats = computed(() => route.path.includes("/stats"));
const isOnEventLive = computed(() => route.path.includes("/live"));
const isOnEventSettings = computed(() => route.path.includes("/settings"));

// Hover state for nav label preview
const hoveredNav = ref<string | null>(null);
const navLabel = computed(() => {
  if (hoveredNav.value) return hoveredNav.value;
  return isOnEventLive.value ? 'nav.liveView' : isOnEventStats.value ? 'nav.historicalStats' : 'nav.settings';
});

// Time display
const now = useNowCustom(1000);
const formattedTime = computed(() => {
  return now.value.toLocaleString(locale.value, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
});

// Logout handler
async function handleLogout() {
  await logout();
}

const items = computed(() => !isLoggedIn.value || !selectedProjectName.value ? [] : !selectedEventName.value ? [
  { icon: "pi pi-folder", label: selectedProjectName.value, route: `/projects/${currentProjectId.value}/events` },
] : [
  { icon: "pi pi-calendar", label: selectedEventName.value, route: `/projects/${currentProjectId.value}/events/${currentEventId.value}/live` }
]);

</script>

<template>
  <div class="min-h-screen flex flex-col">
    <!-- Top Navigation Bar -->

    <nav class="fixed top-0 left-0 right-0 z-50 border-b border-surface-200 shadow-sm"
      style="background-color: rgb(236, 28, 36)">
      <div class="mx-auto px-4 sm:px-6">
        <div class="flex items-center justify-between h-14">
          <!-- Left: Logo + Breadcrumbs -->
          <div class="flex items-center gap-3" style="color: white;     font-size: 1.2em;">
            <router-link to="/projects" class="flex-shrink-0">
              <img class="h-8"
                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFoAAABaBAMAAADKhlwxAAAAAXNSR0IB2cksfwAAAAlwSFlzAAALEwAACxMBAJqcGAAAABVQTFRF7Bwk7jA38FBX9pea83F2+8vN////Xh7zqwAAAj9JREFUWMPlV01TgzAQJUA8M2p7RjtyRjtypjrlTAfL2UKz//8neCghm2Q31Bkv6h7hsR9vd8lLFP0du91st9vH7BqoeO/hYl29CL7XWACAjwX/G7BszEPgPTimAtk8gWeK9b4CwhSTewqknWnqehoNh6Wkj8c+nLrJo3uLoiiK5y5RuTTa025Obeaz5lyPmII7znlDgA28Jl175E4tGOynBZOgjpkTXA9+8THBueSbfMll9OMdAi1GOQradfxgnJ/M48R9oId9zLWr0WUkJ+ZGZf7b3isEk3GJ3NovWhu9nvqlnQ02f04ilWl6YYVeE4kIPX+tTiXDbk7MuA/6yxIXWdpoiVehQnWlViAXPWqGB9QbFQXQEhUmqfVIcCYp8rem2o6rnMrMzAC2Ec/gxENtCCzdSa3wSiJIT+7YynTeCt8TfXdWrDBoAABgVmyi7QYA4HNGK/qc6DIXHXNot1fD/0GLK9AO3/A9dPCIxp2np8r/i7eBifXHt+S3wTYUvqA2jZje3BR8DqETRJtcJBz/IdJFChsUXcBSmT2urGdOP7vIEgdSC2nP/ZMQ7qbtLYUg48LWEQKCqUiHhSaYSuW8lQFxNuWpvL9Yzs+2RTArOGZPpX+U1hx9tmKIWSmXUFKpYWSlFqA1eSjVjJSjxsxToVqtloyatuB7TlXPClm9zKW/8io54VXvyI4DoajJNsSMWGe2akWDuYvDngRzgywqAv3M39Gqb4Cx+HevBcxFDVPXLd4a0Y1x99O30V9iXyGl7yErkDsXAAAAAElFTkSuQmCC"
                alt="OrangeAD" />
            </router-link>
            <Breadcrumb :model="items">
              <template #item="{ item, props }">
                <router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
                  <a :href="href" v-bind="props.action" @click="navigate">
                    <span v-if="item.icon" :class="[item.icon]" style="color: white" />
                    <span>{{ item.label }}</span>
                  </a>
                </router-link>
                <a v-else :href="item.url" :target="item.target" v-bind="props.action">
                  <span>{{ item.label }}</span>
                </a>
              </template>
            </Breadcrumb>
          </div>

          <!-- Right: Event Navigation + Actions -->
          <div class="flex items-center gap-4">
            <TimeController v-if="false" :formattedTime="formattedTime" />
            <template v-if="currentEventId">
              <Transition name="nav-label" mode="out-in">
                <span :key="navLabel" style="color: white; padding-right:0.3em; display: inline-block;">{{ $t(navLabel)
                }}</span>
              </Transition>
              <router-link
                :to="{ name: 'event-live', params: { project_id: currentProjectId, event_id: currentEventId, map_id: lastMapId } }"
                @mouseenter="hoveredNav = 'nav.liveView'" @mouseleave="hoveredNav = null">
                <NavButton icon="pi pi-map-marker" rounded :aria-label="$t('nav.liveView')" :text="!isOnEventLive"
                  p-icon-only :style="`color: ${!isOnEventLive ? 'white' : 'rgb(236, 28, 36)'}`" />
              </router-link>
              <router-link v-if="localhost"
                :to="{ name: 'event-stats', params: { project_id: currentProjectId, event_id: currentEventId } }"
                @mouseenter="hoveredNav = 'nav.historicalStats'" @mouseleave="hoveredNav = null">
                <NavButton icon="pi pi-chart-line" rounded :aria-label="$t('nav.historicalStats')"
                  :text="!isOnEventStats" p-icon-only
                  :style="`color: ${!isOnEventStats ? 'white' : 'rgb(236, 28, 36)'}`" />
              </router-link>
              <!--
            <router-link
              :to="{ name: 'event-settings', params: { project_id: currentProjectId, event_id: currentEventId } }"
              @mouseenter="hoveredNav = 'nav.settings'" @mouseleave="hoveredNav = null">
              <NavButton icon="pi pi-cog" rounded :aria-label="$t('nav.settings')" :text="!isOnEventSettings"
                p-icon-only :style="`color: ${!isOnEventSettings ? 'white' : 'rgb(236, 28, 36)'}`" />
            </router-link> -->
            </template>
            <NavButton v-if="isLoggedIn" icon="pi pi-sign-out" text rounded :aria-label="$t('nav.logout')"
              @click="handleLogout" p-icon-only style="color: white" />
          </div>
        </div>
      </div>
    </nav>


    <!-- Page Content -->
    <div class="mt-14 flex-1" :class="isOnEventLive ? 'overflow-hidden' : 'pt-6 px-4 sm:px-6'">
      <RouterView />
    </div>

    <!-- Footer -->
    <footer v-if="!isOnEventLive" class="border-t border-surface-200 shadow-sm"
      style="background-color: rgb(240, 240, 240)">
      <div class="mx-auto px-2 py-2 flex items-center justify-end">
        <LanguageSwitcher />
      </div>
    </footer>
  </div>
</template>

<style>
a {
  text-decoration: none;
  color: inherit;
}

.nav-label-enter-active,
.nav-label-leave-active {
  transition: opacity 0.1s ease;
}

.nav-label-enter-from,
.nav-label-leave-to {
  opacity: 0;
}

:deep(.p-tooltip) {
  background-color: rgba(0, 0, 0, 0.9);
  color: #ffffff;
  border: 1px solid #333333;
  border-radius: 0.375rem;
  z-index: 9999;
}

:deep(.p-tooltip .p-tooltip-text) {
  font-size: 0.875rem;
}

:deep(.p-tooltip.p-tooltip-top .p-tooltip-arrow) {
  border-top-color: rgba(0, 0, 0, 0.9);
}

:deep(.p-tooltip.p-tooltip-bottom .p-tooltip-arrow) {
  border-bottom-color: rgba(0, 0, 0, 0.9);
}

:deep(.p-tooltip.p-tooltip-left .p-tooltip-arrow) {
  border-left-color: rgba(0, 0, 0, 0.9);
}

:deep(.p-tooltip.p-tooltip-right .p-tooltip-arrow) {
  border-right-color: rgba(0, 0, 0, 0.9);
}
</style>
